/**
 * The colour of every status label in the app.
 *
 * Solid tones with white text, so a status reads at a glance on a busy table.
 * Every page used to keep its own map, and most fell back to grey text on a
 * grey chip for anything they did not list — which is how "more info
 * required" and "pending legal" ended up looking like disabled text. The
 * statusTones test fails if any tone drops below AA contrast with white.
 */

export const TONES = {
  success: '#15803d', // done, or in good standing
  info: '#1d4ed8',    // with procurement or legal
  contract: '#0f766e', // at the contract step
  warning: '#b45309', // waiting on someone to act
  danger: '#b91c1c',  // refused or over
  neutral: '#475569', // not started, inactive, or unknown
};

const MAPS = {
  application: {
    draft: 'neutral',
    submitted: 'info',
    under_review: 'info',
    pending_procurement: 'info',
    pending_legal: 'info',
    more_info_required: 'warning',
    pending_contract_upload: 'contract',
    approved: 'success',
    completed: 'success',
    rejected: 'danger',
  },
  contract: {
    active: 'success',
    renewed: 'success',
    expiring_soon: 'warning',
    pending_upload: 'warning',
    recalled: 'warning',
    termination_recommended: 'warning',
    expired: 'danger',
    terminated: 'danger',
  },
  user: {
    active: 'success',
    pending_verification: 'warning',
    suspended: 'danger',
  },
  registration: {
    approved: 'success',
    pending: 'warning',
    rejected: 'danger',
    profile_incomplete: 'neutral',
  },
};

export const KNOWN_STATUSES = Object.fromEntries(
  Object.entries(MAPS).map(([kind, map]) => [kind, Object.keys(map)])
);

/** `{ bg, color, known }` for a status. Unknown statuses get the neutral tone. */
export const toneFor = (kind, status) => {
  const name = MAPS[kind]?.[String(status || '').toLowerCase()];
  return { bg: TONES[name || 'neutral'], color: '#ffffff', known: Boolean(name) };
};

// WCAG relative luminance, for the contrast test.
const channel = (c) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const luminance = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
};
export const contrastWithWhite = (hex) => (1 + 0.05) / (luminance(hex) + 0.05);
